import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import ListboxComponent from "./ListboxComponent";
import type { AutocompleteProps } from "@mui/material/Autocomplete";

const StyledListItem = styled("li")({
  wordBreak: "break-word", // permite a quebra de linha para palavras longas
  whiteSpace: "normal", // garante que o texto quebre em várias linhas
  padding: "8px", // padding interno para melhorar a aparência
  listStyle: "none", // remove o estilo de lista
  cursor: "pointer", // adiciona um cursor de ponteiro para indicar que é clicável
});

type OptionType = string | { name: string; [key: string]: any };

type VirtualizedAutocompleteProps<T> = Omit<
  AutocompleteProps<T, boolean, boolean, boolean>,
  "renderInput" | "renderOption" | "ListboxComponent"
> & {
  renderOption?: AutocompleteProps<
    T,
    boolean,
    boolean,
    boolean
  >["renderOption"];
  renderInput?: AutocompleteProps<T, boolean, boolean, boolean>["renderInput"];
};

const VirtualizedAutocomplete = <T extends OptionType = OptionType>({
  options,
  value,
  onChange,
  renderOption,
  renderInput,
  getOptionLabel,
  multiple = false,
  size = "small",
  fullWidth = false,
  loading = false,
  ...props
}: VirtualizedAutocompleteProps<T>) => {
  const defaultGetOptionLabel = (option: string | T) =>
    typeof option === "string" ? option : option.name ?? "";

  const defaultRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string | T,
    { index }: any
  ) => (
    <StyledListItem {...props} data-index={index}>
      {typeof option === "string" ? option : option.name}
    </StyledListItem>
  );

  const defaultRenderInput = (params: any) => (
    <TextField
      {...params}
      color="primary"
      label="Item"
      slotProps={{
        input: {
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? <div>Loading...</div> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        },
      }}
    />
  );

  return (
    <Autocomplete
      options={options}
      value={value}
      loading={loading}
      onChange={onChange}
      ListboxComponent={ListboxComponent}
      multiple={multiple}
      size={size}
      fullWidth={fullWidth}
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      renderOption={renderOption || defaultRenderOption}
      renderInput={
        renderInput ||
        ((params) => <TextField {...params} color="primary" label="Item" />)
      }
      {...props}
    />
  );
};

export default VirtualizedAutocomplete;
